import {
  useRouter,
  useRouterState,
  type RouterHistory,
} from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'

import { ConfirmDialog, useScopedKeydown } from '@aeris/ui'

import { useAuth } from '../../lib/auth-context.ts'
import { desktopCommandFor } from '../../lib/desktop/desktop-shortcuts.ts'
import {
  layoutKey,
  readLayout,
  serializeLayout,
  writeLayout,
} from '../../lib/desktop/layout-storage.ts'
import { isSingleInstance } from '../../lib/desktop/window-defaults.ts'
import {
  createDesktopStore,
  planOpen,
  useDesktopSelector,
} from '../../lib/desktop/window-store.ts'
import { useVisibleModules } from '../../lib/use-visible-modules.ts'
import {
  DesktopWorkspaceContext,
  moduleForPath,
  moduleRoute,
  moduleTitle,
  type DesktopWorkspace,
} from '../../lib/workspace.ts'
import { BrandLogo } from '../brand-logo.tsx'
import { ModuleSearch } from '../module-search.tsx'
import { StoreOrderAlert } from '../store-order-alert.tsx'

import { DesktopMenubar } from './desktop-menubar.tsx'
import { DesktopToolbar } from './desktop-toolbar.tsx'
import { Taskbar } from './taskbar.tsx'
import { WindowHost } from './window-host.tsx'

/**
 * A área de trabalho do Modo PC — F138.
 *
 * Uma janela do Aeris, e dentro dela: a área onde as telas abrem como janelas,
 * e a barra de tarefas embaixo. Substitui a barra lateral e o topo do Modo
 * Site; as TELAS são as mesmas (Master Specification §26, ADR-0037).
 *
 * `workspace.open()` de qualquer tela — o Ctrl+K, a grade do Início, o
 * "Cobrar" do Financeiro — chega aqui pelo `DesktopWorkspaceContext` e vira
 * uma janela, ou traz para a frente a que já existe.
 */
export const DesktopShell = (): ReactNode => {
  const [store] = useState(() => createDesktopStore())
  const windows = useDesktopSelector(store, (state) => state.windows)
  const [searchOpen, setSearchOpen] = useState(false)

  const areaRef = useRef<HTMLDivElement>(null)
  /** A navegação de cada janela, para abrir um registro numa que já existe. */
  const histories = useRef(new Map<string, RouterHistory>())
  /** Quantas janelas filhas cada janela tem abertas agora. */
  const childCounts = useRef(new Map<string, number>())

  const outer = useRouter()
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  /** Onde a página estava quando o Modo PC ligou: vira a primeira janela. */
  const firstPath = useRef(pathname)

  const { session } = useAuth()
  const context = session?.context ?? null
  /*
   * Trocar de loja remonta as janelas, com a mesma posição e a mesma tela: um
   * formulário pela metade com dados da loja anterior não pode sobreviver à
   * troca.
   */
  const contextKey =
    context === null
      ? 'sem-conta'
      : `${context.tenantId}:${context.companyId ?? ''}:${context.branchId ?? ''}`

  // O tamanho da área, que é o que limita onde uma janela pode ficar.
  useEffect(() => {
    const area = areaRef.current
    if (area === null) return

    const measure = (): void => {
      store.dispatch({
        type: 'setBounds',
        bounds: {
          w: Math.round(area.clientWidth),
          h: Math.round(area.clientHeight),
        },
      })
    }

    measure()
    const observer = new ResizeObserver(measure)
    observer.observe(area)
    return () => {
      observer.disconnect()
    }
  }, [store])

  const desktop = useMemo<DesktopWorkspace>(
    () => ({
      open: ({ module, href, title, newInstance, carriesEntity }) => {
        const request = { module, href, title, newInstance }
        /*
         * Um registro pedido para uma tela que já tem janela: a janela vai até
         * ele, em vez de só subir mostrando o que tinha antes (F090).
         */
        const plan = planOpen(store.getState(), request)
        if (plan.kind === 'focus' && carriesEntity) {
          /*
           * Com uma ficha aberta naquela janela, navegá-la trocaria o registro
           * debaixo do formulário: o registro pedido abre em outra janela.
           */
          if (
            (childCounts.current.get(plan.id) ?? 0) > 0 &&
            !isSingleInstance(module)
          ) {
            store.dispatch({
              type: 'open',
              request: { ...request, newInstance: true },
            })
            return
          }
          histories.current.get(plan.id)?.push(href)
        }
        store.dispatch({ type: 'open', request })
      },
    }),
    [store]
  )

  /*
   * Onde as janelas desta pessoa, nesta conta, ficam guardadas neste
   * computador (`layout-storage.ts`).
   */
  const userId = session?.user.id ?? null
  const tenantId = context?.tenantId ?? null
  const storageKey =
    userId === null || tenantId === null ? null : layoutKey(userId, tenantId)
  const visible = useVisibleModules({ includeTabs: true })
  /** Para qual chave as janelas já foram restauradas. */
  const restoredFor = useRef<string | null>(null)

  /*
   * Ao entrar: as janelas guardadas, ou a tela onde a página estava, ou o
   * Início. Uma vez por chave — trocar de conta fecha as janelas e traz as
   * daquela conta.
   *
   * Uma tela guardada que a pessoa não pode mais abrir (o administrador tirou,
   * o papel mudou) não volta. Uma tela sem módulo também não: é uma página de
   * exceção, e não uma janela de trabalho.
   */
  useEffect(() => {
    if (storageKey === null || restoredFor.current === storageKey) return
    if (restoredFor.current !== null) store.dispatch({ type: 'closeAll' })
    restoredFor.current = storageKey

    const saved = readLayout(storageKey)
    const allowed = new Set(visible.map((entry) => entry.module))
    const kept = (saved?.windows ?? []).flatMap((entry, index) => {
      const module = moduleForPath(entry.pathname)
      if (module === null || !allowed.has(module)) return []
      return [
        {
          index,
          window: {
            module,
            href: entry.pathname,
            title: moduleTitle(module),
            rect: entry.rect,
            mode: entry.mode,
            wasMaximized: entry.wasMaximized,
          },
        },
      ]
    })

    if (kept.length > 0) {
      const activeIndex = kept.findIndex(
        ({ index }) => index === saved?.activeIndex
      )
      store.dispatch({
        type: 'hydrate',
        windows: kept.map(({ window }) => window),
        activeIndex: activeIndex === -1 ? null : activeIndex,
      })
      return
    }

    const module = moduleForPath(firstPath.current) ?? 'inicio'
    desktop.open({
      module,
      href: moduleRoute(module),
      title: moduleTitle(module),
      newInstance: false,
      carriesEntity: false,
    })
  }, [storageKey, store, desktop, visible])

  /*
   * Guardar: 300 ms depois da última mudança, e quando a página vai embora.
   *
   * **Nunca ao desmontar.** Sair da conta desmonta a área de trabalho, e gravar
   * nessa hora trocaria o layout guardado por uma área vazia. E só depois que
   * esta chave foi restaurada, pelo mesmo motivo.
   */
  useEffect(() => {
    if (storageKey === null) return

    let timer = 0
    const save = (): void => {
      if (restoredFor.current !== storageKey) return
      writeLayout(storageKey, serializeLayout(store.getState()))
    }
    const unsubscribe = store.subscribe(() => {
      window.clearTimeout(timer)
      timer = window.setTimeout(save, 300)
    })
    const onPageHide = (): void => {
      window.clearTimeout(timer)
      save()
    }
    window.addEventListener('pagehide', onPageHide)

    return () => {
      unsubscribe()
      window.clearTimeout(timer)
      window.removeEventListener('pagehide', onPageHide)
    }
  }, [store, storageKey])

  /*
   * Trocar de loja com uma ficha aberta pergunta antes: a troca remonta as
   * janelas, e o que estava sendo digitado se perderia sem aviso.
   */
  const [pendingSwitch, setPendingSwitch] = useState<
    ((proceed: boolean) => void) | null
  >(null)

  const onBeforeSwitch = useCallback((): Promise<boolean> => {
    const open = [...childCounts.current.values()].reduce(
      (total, count) => total + count,
      0
    )
    if (open === 0) return Promise.resolve(true)
    return new Promise<boolean>((resolve) => {
      setPendingSwitch(() => resolve)
    })
  }, [])

  const onChildren = useCallback((id: string, count: number) => {
    if (count === 0) childCounts.current.delete(id)
    else childCounts.current.set(id, count)
  }, [])

  const onHistory = useCallback((id: string, history: RouterHistory | null) => {
    if (history === null) histories.current.delete(id)
    else histories.current.set(id, history)
  }, [])

  const onLeaveShell = useCallback(
    (id: string, href: string) => {
      store.dispatch({ type: 'close', id })
      outer.history.push(href)
    },
    [store, outer]
  )

  /*
   * Os atalhos da área de trabalho (`desktop-shortcuts.ts`): Ctrl+K, Ctrl+Tab,
   * Ctrl+Shift+Tab e Ctrl+F4. Nenhum deles é tecla de tela, então valem com o
   * foco em qualquer janela.
   */
  useScopedKeydown((event) => {
    const command = desktopCommandFor(event)
    if (command === null) return
    event.preventDefault()

    if (command.kind === 'search') {
      setSearchOpen(true)
    } else if (command.kind === 'cycle') {
      store.dispatch({ type: 'cycle', direction: command.direction })
    } else {
      const active = store.getState().activeId
      if (active !== null) store.dispatch({ type: 'close', id: active })
    }
  })

  return (
    <DesktopWorkspaceContext value={desktop}>
      <div className="mt-(--support-banner-h) flex h-[calc(100dvh-var(--support-banner-h))] flex-col overflow-hidden bg-(--desktop-bg)">
        <DesktopMenubar store={store} />
        <DesktopToolbar />

        <div
          ref={areaRef}
          role="region"
          aria-label="Área de trabalho"
          // `isolate`: os `z-index` das janelas valem só aqui dentro, e os
          // menus e dicas do kit, que abrem no corpo da página, ficam por cima.
          className="relative isolate min-h-0 flex-1 overflow-hidden"
        >
          {windows.length === 0 ? (
            <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center gap-3 text-(--text-muted)">
              <BrandLogo
                variant="mark"
                className="h-14 w-14 rounded-(--radius-md)"
              />
              <p className="text-(length:--text-sm)">
                Abra uma tela pela barra de tarefas, ou com Ctrl K.
              </p>
            </div>
          ) : null}

          <div key={contextKey} className="contents">
            {windows.map((entry) => (
              <WindowHost
                key={entry.id}
                store={store}
                id={entry.id}
                onHistory={onHistory}
                onChildren={onChildren}
                onLeaveShell={onLeaveShell}
              />
            ))}
          </div>
        </div>

        <Taskbar
          store={store}
          onSearch={() => {
            setSearchOpen(true)
          }}
          onBeforeSwitch={onBeforeSwitch}
        />
      </div>

      <ModuleSearch open={searchOpen} onOpenChange={setSearchOpen} />

      {/*
       * O pedido da loja (F224), dentro da área de trabalho (F248): "Abrir"
       * abre a janela de Pedidos da loja pelo `DesktopWorkspaceContext`.
       */}
      <StoreOrderAlert />

      <ConfirmDialog
        open={pendingSwitch !== null}
        onOpenChange={(next) => {
          if (next) return
          pendingSwitch?.(false)
          setPendingSwitch(null)
        }}
        title="Trocar de loja"
        description="Há uma ficha aberta numa janela. Trocar de loja fecha a ficha, e o que foi digitado nela se perde."
        confirmLabel="Trocar de loja"
        tone="neutral"
        onConfirm={() => {
          pendingSwitch?.(true)
          setPendingSwitch(null)
        }}
      />
    </DesktopWorkspaceContext>
  )
}
